import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { OWNER_ALERT_TYPES, type OwnerAlertDto, type OwnerAlertType } from '@his/shared';
import { get, qs } from '@/lib/api';
import { dateTime } from '@/lib/utils';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select } from '@/components/ui/primitives';
import { visibleInterval } from '@/lib/polling';

const ALL = '';
const AUTO_REFRESH_MS = 60_000;

export const ALERT_TYPE_LABELS: Record<OwnerAlertType, { label: string; variant: 'warning' | 'destructive' | 'success' | 'default' }> = {
  STALE_WARNING: { label: 'Stale warning', variant: 'warning' },
  LOCKED: { label: 'Locked', variant: 'destructive' },
  UNLOCKED: { label: 'Unlocked', variant: 'success' },
  FINGERPRINT_MISMATCH: { label: 'Fingerprint mismatch', variant: 'destructive' },
  UPDATE_FAILED: { label: 'Update failed', variant: 'destructive' },
  SYNC_ROWS_PARKED: { label: 'Records not syncing', variant: 'warning' },
  CUSTOMER_SILENT: { label: 'Customer silent', variant: 'warning' },
  NEW_VERSION: { label: 'New version', variant: 'default' },
  STEP_UP_LOCKOUT: { label: 'Account locked', variant: 'destructive' },
  OWNER_LOGIN_ATTACK: { label: 'Owner sign-in attack', variant: 'destructive' },
  SYNC_RESTORE_DETECTED: { label: 'Restored from backup', variant: 'destructive' },
  SYNC_SEQUENCE_REGRESSION: { label: 'Older changes received', variant: 'warning' },
  SYNC_PAUSED_CLOUD_OLDER: { label: 'Online service out of date', variant: 'warning' },
  TENANT_FROZEN: { label: 'Facility frozen', variant: 'destructive' },
  TENANT_PURGE_FAILED: { label: 'Purge incomplete', variant: 'destructive' },
  LICENCE_REJECTED: { label: 'Licence rejected', variant: 'destructive' },
  LICENCE_NOT_APPLIED: { label: 'Licence not applied', variant: 'warning' },
  LICENCE_VERSION_AHEAD: { label: 'Licence ahead of cloud', variant: 'destructive' },
  LICENCE_KEY_PROBLEM: { label: 'Licence key problem', variant: 'destructive' },
  PROFILE_CONFLICT: { label: 'Profile conflict', variant: 'warning' },
  FINGERPRINT_MISSING: { label: 'Fingerprint missing', variant: 'warning' },
  SUBSCRIPTION_ENDING: { label: 'Subscription ending', variant: 'warning' },
  LIFECYCLE_REPORT: { label: 'Lifecycle report', variant: 'default' },
  SUPPORT_SESSION_OPENED: { label: 'Owner signed in', variant: 'default' },
  CLOSURE_SCHEDULED: { label: 'Closure scheduled', variant: 'destructive' },
  EXPORT_READY: { label: 'Data export ready', variant: 'default' },
  BRANCH_REQUESTED: { label: 'Branch requested', variant: 'warning' },
  EMAIL_PAUSED: { label: 'Email paused', variant: 'warning' },
};

interface TenantOption {
  id: string;
  slug: string;
  name: string;
}

/** Every alert the platform raised, delivered or not — the feed the plan promises even when email and SMS fail. */
export function OwnerAlertsFeed() {
  const [tenantId, setTenantId] = useState(ALL);
  const [type, setType] = useState(ALL);

  const tenants = useQuery({
    queryKey: ['tenants', 'for-alert-filter'],
    queryFn: () => get<{ data: TenantOption[] }>(`/platform/tenants${qs({ limit: 100 })}`),
    staleTime: 60_000,
  });

  const alerts = useQuery({
    queryKey: ['owner-alerts', tenantId, type],
    queryFn: () => get<OwnerAlertDto[]>(`/owner/alerts${qs({ tenantId: tenantId || undefined, type: type || undefined, limit: 200 })}`),
    refetchInterval: visibleInterval(AUTO_REFRESH_MS),
    placeholderData: (previous) => previous,
  });

  return (
    <div>
      <SearchFilterBar
        filters={
          <>
            <FilterField label="Facility">
              <Select className="w-64" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                <option value={ALL}>All facilities</option>
                {(tenants.data?.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.slug} — {t.name}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label="Event">
              <Select className="w-56" value={type} onChange={(e) => setType(e.target.value)}>
                <option value={ALL}>All events</option>
                {OWNER_ALERT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ALERT_TYPE_LABELS[t].label}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        trailing={
          <Button variant="outline" size="sm" className="gap-1.5" disabled={alerts.isFetching} onClick={() => void alerts.refetch()}>
            <RefreshCw className={alerts.isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} /> Refresh
          </Button>
        }
        onReset={
          tenantId || type
            ? () => {
                setTenantId(ALL);
                setType(ALL);
              }
            : undefined
        }
      />
      <DataTable
        rows={alerts.data}
        loading={alerts.isLoading} refreshing={alerts.isFetching}
        error={alerts.error}
        rowKey={(row) => row.id}
        empty="No alerts yet."
        columns={[
          { header: 'When', sort: (row) => row.at, cell: (row) => <span className="whitespace-nowrap text-xs text-muted-foreground">{dateTime(row.at)}</span> },
          {
            header: 'Event',
            sort: (row) => row.type,
            cell: (row) => <Badge variant={ALERT_TYPE_LABELS[row.type].variant}>{ALERT_TYPE_LABELS[row.type].label}</Badge>,
          },
          {
            header: 'Facility',
            sort: (row) => row.tenantSlug,
            cell: (row) =>
              row.tenantSlug ? (
                <span>
                  <span className="font-mono text-xs">{row.tenantSlug}</span>
                  <span className="ms-1.5 text-muted-foreground">{row.tenantName}</span>
                </span>
              ) : (
                <span className="text-muted-foreground">Platform</span>
              ),
          },
          {
            header: 'Message',
            sort: (row) => row.message,
            cell: (row) => (
              <div className="space-y-0.5">
                <p className="text-sm">{row.message}</p>
                {row.error && <p className="font-mono text-[11px] text-destructive">{row.error}</p>}
              </div>
            ),
          },
          {
            header: 'Delivered',
            cell: (row) => (
              <div className="flex gap-1.5">
                <Badge variant={row.sentEmail ? 'success' : 'muted'}>Email</Badge>
                <Badge variant={row.sentSms ? 'success' : 'muted'}>SMS</Badge>
              </div>
            ),
          },
        ]}
        footer={<span className="text-xs">{alerts.data?.length ?? 0} alert(s) · refreshes every 60 s while this tab is visible</span>}
      />
    </div>
  );
}
